import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import type { MutableRefObject, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { useTenantScope } from '@/hooks/useTenantScope';
import { useUIStore } from '@/stores/useUIStore';
import { ticketCreateHref } from '@/routes/paths';

export interface HotkeyOverrides {
  /** Kept for page search fields; `/` is no longer a global shortcut. */
  searchInputId?: string;
  onNewTicket?: () => void;
  onEscape?: () => void;
}

interface HotkeyContextValue {
  overridesRef: MutableRefObject<HotkeyOverrides>;
  setOverrides: (next: HotkeyOverrides | null) => void;
}

const HotkeyContext = createContext<HotkeyContextValue | null>(null);

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable ||
    target.getAttribute('role') === 'combobox'
  );
}

export function HotkeyProvider({ children }: { children: ReactNode }): JSX.Element {
  const overridesRef = useRef<HotkeyOverrides>({});
  const setOverrides = useCallback((next: HotkeyOverrides | null) => {
    overridesRef.current = next ?? {};
  }, []);
  const value = useMemo<HotkeyContextValue>(() => ({ overridesRef, setOverrides }), [setOverrides]);
  return createElement(HotkeyContext.Provider, { value }, children);
}

/** Workbench (and similar) register page-specific `n` and Escape handlers. */
export function useHotkeyOverrides(overrides: HotkeyOverrides): void {
  const ctx = useContext(HotkeyContext);
  const overridesRef = useRef(overrides);
  overridesRef.current = overrides;

  useEffect(() => {
    if (!ctx) return;
    ctx.setOverrides({
      searchInputId: overrides.searchInputId,
      onNewTicket: () => overridesRef.current.onNewTicket?.(),
      onEscape: () => overridesRef.current.onEscape?.(),
    });
    return () => ctx.setOverrides(null);
  }, [ctx, overrides.searchInputId]);
}

export interface UseAppHotkeysOptions {
  paletteOpen: boolean;
  onOpenPalette: () => void;
  onClosePalette: () => void;
}

/**
 * App-wide keyboard shortcuts. Ignored while typing in a field.
 *
 * `n` new ticket · `Cmd/Ctrl+K` command palette. Escape still closes overlays.
 */
export function useAppHotkeys({ paletteOpen, onOpenPalette, onClosePalette }: UseAppHotkeysOptions): void {
  const navigate = useNavigate();
  const { isStaff, canCreateTickets } = usePermissions();
  const tenantScope = useTenantScope();
  const setMobileNavOpen = useUIStore((state) => state.setMobileNavOpen);
  const setAiAssistantOpen = useUIStore((state) => state.setAiAssistantOpen);
  const overridesRef = useContext(HotkeyContext)?.overridesRef ?? { current: {} };

  useEffect(() => {
    const openNewTicket = (): void => {
      if (overridesRef.current.onNewTicket) {
        overridesRef.current.onNewTicket();
        return;
      }
      if (!canCreateTickets) return;
      void navigate(ticketCreateHref({ clienteId: isStaff ? tenantScope : undefined }));
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        if (paletteOpen) {
          event.preventDefault();
          onClosePalette();
          return;
        }
        overridesRef.current.onEscape?.();
        setMobileNavOpen(false);
        setAiAssistantOpen(false);
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        if (paletteOpen) onClosePalette();
        else onOpenPalette();
        return;
      }

      if (paletteOpen) return;
      if (isTypingTarget(event.target)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === 'n' || event.key === 'N') {
        event.preventDefault();
        openNewTicket();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    canCreateTickets,
    isStaff,
    navigate,
    onClosePalette,
    onOpenPalette,
    paletteOpen,
    setAiAssistantOpen,
    setMobileNavOpen,
    tenantScope,
  ]);
}
